import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Calendar, Share2, Download, CheckCircle, Loader2, AlertCircle, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  appsBillingApiTicketsGetTicketBySession,
  appsBillingApiTicketsGetTicketQrUrl,
} from '@/lib/api';
import type { TicketWithEventResponse } from '@/lib/api/generated/models';

/**
 * Generate an iCalendar (.ics) file content for the event
 */
function generateICSContent(ticket: TicketWithEventResponse): string {
  const eventDate = ticket.event_meeting_datetime
    ? new Date(ticket.event_meeting_datetime)
    : new Date();

  // Format date as YYYYMMDDTHHMMSSZ
  const formatDate = (date: Date): string => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const startDate = formatDate(eventDate);
  // Default event duration: 4 hours
  const endDate = formatDate(new Date(eventDate.getTime() + 4 * 60 * 60 * 1000));
  const now = formatDate(new Date());

  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//ExodusADV//Rally Ticket//EN
BEGIN:VEVENT
UID:${ticket.id}@exodusadv.com
DTSTAMP:${now}
DTSTART:${startDate}
DTEND:${endDate}
SUMMARY:${ticket.event_title}
DESCRIPTION:Rally Ticket - ${ticket.tier_name}
LOCATION:${ticket.event_meeting_location || 'TBD'}
END:VEVENT
END:VCALENDAR`;
}

/**
 * Download an ICS file for the event
 */
function downloadICS(ticket: TicketWithEventResponse) {
  const icsContent = generateICSContent(ticket);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${ticket.event_title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generate a Google Calendar link for the event
 */
function generateGoogleCalendarLink(ticket: TicketWithEventResponse): string {
  const eventDate = ticket.event_meeting_datetime
    ? new Date(ticket.event_meeting_datetime)
    : new Date();

  const formatForGoogle = (date: Date): string => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const startDate = formatForGoogle(eventDate);
  const endDate = formatForGoogle(new Date(eventDate.getTime() + 4 * 60 * 60 * 1000));

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: ticket.event_title,
    dates: `${startDate}/${endDate}`,
    details: `Rally Ticket - ${ticket.tier_name}`,
    location: ticket.event_meeting_location || '',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

const TicketSuccess = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const sessionId = searchParams.get('session_id');

  const [ticket, setTicket] = useState<TicketWithEventResponse | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [qrLoading, setQrLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch ticket details
  useEffect(() => {
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    const fetchTicket = async () => {
      try {
        const response = await appsBillingApiTicketsGetTicketBySession(sessionId);
        setTicket(response.data);

        // If ticket has a QR code, fetch the signed URL
        if (response.data.has_qr_code) {
          setQrLoading(true);
          try {
            const qrResponse = await appsBillingApiTicketsGetTicketQrUrl(response.data.id);
            setQrUrl(qrResponse.data.qr_url);
          } catch (qrErr) {
            console.error('Failed to fetch QR code:', qrErr);
            // Don't fail the whole page if QR fetch fails
          } finally {
            setQrLoading(false);
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch ticket:', err);
        setError(err.message || 'Failed to load ticket details');
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [sessionId]);

  const handleShare = async () => {
    if (!ticket) return;

    const shareData = {
      title: `Rally Ticket - ${ticket.event_title}`,
      text: `I'm going to ${ticket.event_title}!`,
      url: window.location.origin + `/rallies/${ticket.event_id}`,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      // Fallback: copy link to clipboard
      try {
        await navigator.clipboard.writeText(shareData.url);
        toast({
          title: 'Link copied',
          description: 'Event link copied to clipboard',
        });
      } catch (err) {
        console.error('Copy failed:', err);
        toast({
          title: 'Error',
          description: 'Failed to copy link',
          variant: 'destructive',
        });
      }
    }
  };

  const handleAddToCalendar = () => {
    if (!ticket) return;
    downloadICS(ticket);
  };

  const handleGoogleCalendar = () => {
    if (!ticket) return;
    window.open(generateGoogleCalendarLink(ticket), '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-adv-dark flex items-center justify-center pt-28 pb-16">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-adv-orange mx-auto mb-4" />
          <p className="text-adv-lightGray">Loading your ticket...</p>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-adv-dark flex items-center justify-center pt-28 pb-16">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Ticket Not Found</h1>
          <p className="text-adv-lightGray mb-6">
            {error || "We couldn't find your ticket. It may still be processing."}
          </p>
          <Link to="/">
            <Button variant="outline">Return Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const eventDate = ticket.event_meeting_datetime
    ? new Date(ticket.event_meeting_datetime).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'Date TBD';

  return (
    <div className="min-h-screen bg-adv-dark pt-28 pb-16">
      <div className="container-custom max-w-2xl mx-auto px-4">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">You're In!</h1>
          <p className="text-adv-lightGray">
            Your ticket for {ticket.event_title} has been confirmed.
          </p>
        </div>

        {/* Ticket Card */}
        <div className="glass-card rounded-lg overflow-hidden mb-6">
          {/* Ticket Header */}
          <div className="bg-adv-orange/20 p-6 border-b border-adv-orange/30">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-white mb-1">{ticket.event_title}</h2>
                <p className="text-adv-lightGray">{ticket.tier_name}</p>
              </div>
              <Ticket className="h-8 w-8 text-adv-orange" />
            </div>
          </div>

          {/* Ticket Body */}
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-adv-lightGray mb-1">Date</p>
                <p className="text-white font-medium">{eventDate}</p>
              </div>
              <div>
                <p className="text-sm text-adv-lightGray mb-1">Location</p>
                <p className="text-white font-medium">{ticket.event_meeting_location || 'TBD'}</p>
              </div>
              <div>
                <p className="text-sm text-adv-lightGray mb-1">Amount Paid</p>
                <p className="text-white font-medium">${ticket.amount_paid.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-adv-lightGray mb-1">Status</p>
                <p className="text-green-400 font-medium capitalize">{ticket.status}</p>
              </div>
            </div>

            {/* QR Code */}
            {ticket.has_qr_code && (
              <div className="border-t border-white/10 pt-6">
                <p className="text-sm text-adv-lightGray mb-3 text-center">
                  Show this QR code at check-in
                </p>
                <div className="flex justify-center">
                  {qrLoading ? (
                    <div className="w-48 h-48 bg-white/5 rounded-lg flex items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-adv-orange" />
                    </div>
                  ) : qrUrl ? (
                    <img
                      src={qrUrl}
                      alt="Ticket QR Code"
                      className="w-48 h-48 rounded-lg bg-white p-2"
                    />
                  ) : (
                    <div className="w-48 h-48 bg-white/5 rounded-lg flex items-center justify-center">
                      <p className="text-adv-lightGray text-sm text-center px-4">
                        QR code unavailable
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={handleAddToCalendar}
              variant="outline"
              className="w-full"
            >
              <Download className="w-4 h-4 mr-2" />
              Download .ics
            </Button>
            <Button
              onClick={handleGoogleCalendar}
              variant="outline"
              className="w-full"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Google Calendar
            </Button>
          </div>
          <Button
            onClick={handleShare}
            variant="outline"
            className="w-full"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share Event
          </Button>
        </div>

        {/* Links */}
        <div className="mt-8 text-center space-y-2">
          <Link
            to={`/profile/${(window as any).__DJANGO_DATA__?.user?.id || ''}`}
            className="text-adv-orange hover:underline block"
          >
            View all my tickets
          </Link>
          <Link to="/" className="text-adv-lightGray hover:text-white block">
            Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TicketSuccess;
